import dayjs from 'dayjs';
import { createContext, ReactNode, useContext, useState } from 'react';
import toast from 'react-hot-toast';
import { handleError } from '../utils/Error';
import { Server } from '../services/Server';
import { useMutation } from '@tanstack/react-query';
import { useAppContext } from './AppContext';

interface FormContextType {
  formData: any;
  setFormData: (data: any) => void;
  getFormDataItem: (data: any, name: string) => any;
  updateDataValue: (prevFormData: any, name: string, value: any) => void;
  updateFormValue: (name: string, value: any) => void;
  results: any;
  setResults: (results: any) => void;
  handleChange: (e: any, field: any) => void;
  handleSpecialArrayFieldChange: (e: any, field: any, index: number) => void;
  clearSoilingRatioSelections: (ratioKey: string, fileUidToRemove?: string) => void;
}

const FormContext = createContext<FormContextType | undefined>(undefined);

interface FormContextProviderProps {
  children: ReactNode;
}

const FormContextProvider: React.FC<FormContextProviderProps> = ({ children }) => {
  const { modified, setModified, role } = useAppContext(); // Add role here
  const { postValidateCSVAPI, postSoilingSensorDataFileAPI, postSensorDataAPI } = Server();
  const [formData, setFormData] = useState({});
  const [results, setResults] = useState({});

  const updateFormValue = (name: string, value: any) => {
    if (name.includes('.')) {
      setFormData((prevFormData) => {
        return {
          ...updateDataValue(prevFormData, name, value),
        };
      });
    } else {
      setFormData((prevFormData) => ({
        ...prevFormData,
        [name]: value,
      }));
    }
  };

  const updateDataValue = (prevFormData: any, name: string, value: any) => {
    const keys = name.split('.');
    keys.reduce((acc, key, index) => {
      if (!acc.hasOwnProperty(key)) {
        acc[key] = {};
      }
      if (index === keys.length - 1) {
        acc[key] = value;
      }
      return acc[key];
    }, prevFormData);
    return prevFormData;
  };

  const handleChange = (e: any, field: any) => {
    let { name, value, type, checked } = e.target;
    let val = value;

    // Special logic for cleaning packages
    if (name.startsWith('cleaning_packages.') && type === 'number') {
      val = val === '' ? 0 : Number(value);
      if (field.hasOwnProperty('max') && field.hasOwnProperty('min') && val !== null) {
        let field_max = typeof field.max === 'string' ? getFormDataItem(formData, field.max) : field.max;
        val = val > field_max ? field_max : val;
        val = val < field.min ? field.min : val;
      }
    } else if (type === 'number') {
      // Normal logic for other numeric fields
      val = val === '' ? null : Number(value);
      if (field.hasOwnProperty('max') && field.hasOwnProperty('min') && val !== null) {
        let field_max = typeof field.max === 'string' ? getFormDataItem(formData, field.max) : field.max;
        val = val > field_max ? field_max : val;
        val = val < field.min ? field.min : val;
      }
    }

    if (type === 'array') {
      val = value.split(',').map((x: string) => Number(x));
      val = val.length && val[0] !== 0 ? val : null;
    }
    if (type === 'sarray') {
      val = value.split(',').map((item: string) => {
        return { date: dayjs(item) };
      }); //.map((x) => x));
      val = val.length && val[0] !== '' ? val : null;
    }
    if (type === 'checkbox') {
      val = checked;
    }
    if (type === 'switch') {
      val = checked;
    }
    if (type === 'file') {
      let file = e.target.files?.[0];
      let params = {};

      if (!file) return;
      if (field.hasOwnProperty('params')) {
        // Evaluate parameters that contain form data references
        params = Object.entries(field.params).reduce((acc: any, [key, value]) => {
          if (typeof value === 'string' && value.includes('.')) {
            // This is a reference to form data, evaluate it
            acc[key] = getFormDataItem(formData, value);
          } else {
            // This is a literal value, use it as is
            acc[key] = value;
          }
          return acc;
        }, {});
      }

      // Determine single_year based on role
      const singleYear = !['ex_post_analysis', 'operational_strategy'].includes(role);

      // Special handling for soiling sensor data uploads
      if (name === 'soiling_sensor_data.pattern' || name.match(/^soiling_sensor_data_\d+\.pattern$/)) {
        // Require project_id to be present before allowing upload
        if (!params || !(params as any).project_id) {
          toast.error('Please save the project before uploading soiling sensor files.');
          return;
        }
        postSoilingSensorData.mutate({ file, field, params, name } as any);
        // Don't update the pattern field with the filename for soiling sensor data
        return;
      } else {
        validateCSV.mutate({ file, params: { ...params, single_year: singleYear }, field, name } as any);
      }
    }
    updateFormValue(name, val);
    if (field.hasOwnProperty('onChange')) {
      field.onChange(val);
    }
    if (!modified) {
      setModified(true);
      setResults({});
    }
  };

  const handleSpecialArrayFieldChange = (e: any, field: any, index: number) => {
    let val: any = getFormDataItem(formData, field.name);
    val[index] = e.target.value;
    handleChange({ target: { name: field.name, value: val, type: e.type, checked: e.checked } }, field);
  };

  const validateCSV = useMutation({
    mutationFn: ({ file, params }: any) => {
      return postValidateCSVAPI(file, params);
    },
    onSuccess: (data, variables) => {
      const { field, name } = variables;
      updateFormValue(name, data);
      if (field.hasOwnProperty('onChange')) {
        field.onChange(data);
      }
      if (!modified) {
        setModified(true);
        setResults({});
      }

      if (data.warning) {
        toast(data.warning, {
          icon: '⚠️',
        });
      } else {
        toast.success('CSV file uploaded');
      }
    },
    onError: (error: any) => {
      handleError(error);
    },
  });

  // Consolidated clearSoilingRatioSelections function
  const clearSoilingRatioSelections = (ratioKey: string, fileUidToRemove?: string) => {
    const existingData = getFormDataItem(formData, ratioKey);
    if (existingData) {
      // If fileUidToRemove is provided, filter out sensor_files that reference it
      // Otherwise, clear all selections
      const filteredSensorFiles = fileUidToRemove ? existingData.sensor_files?.filter((sf: any) => sf.uid !== fileUidToRemove) || [] : [];

      // If there are no more sensor files with selections, clear everything
      if (filteredSensorFiles.length === 0 || !filteredSensorFiles.some((sf: any) => sf.selected?.length > 0)) {
        updateFormValue(ratioKey, {
          combined_pattern: null,
          sensor_files: [],
          hsu_selected: false,
        });
      } else {
        // Keep other sensor files but clear combined pattern since selection changed
        updateFormValue(ratioKey, {
          ...existingData,
          combined_pattern: null,
          sensor_files: filteredSensorFiles,
        });
      }
    }
  };

  const postSoilingSensorData = useMutation({
    mutationFn: ({ file, params }: any) => postSoilingSensorDataFileAPI(file, params),
    onSuccess: (data, variables) => {
      const { field, name, params } = variables;
      // Extract file_uid from response and update the block's file_uid field
      // The response should contain the UID directly or in a file_uid field
      const fileUid = data?.file_uid || data; // Handle both cases: {file_uid: "xxx"} or just "xxx"

      if (fileUid) {
        // Determine the block name from the field name
        const blockName = name.replace('.pattern', '');
        const fileUidFieldName = `${blockName}.file_uid`;

        // Get the old file_uid before updating
        const oldFileUid = getFormDataItem(formData, fileUidFieldName);

        updateFormValue(fileUidFieldName, fileUid);

        // Use the consolidated function
        clearSoilingRatioSelections('soiling_ratio_operational', oldFileUid);
        clearSoilingRatioSelections('soiling_ratio_operational_ex_post', oldFileUid);

        // Fetch the actual data using the file_uid
        if (params.project_id && params.latitude && params.longitude) {
          // Determine sensor type from the block, default to rde300
          const sensorType = getFormDataItem(formData, `${blockName}.type`) || 'rde300';
          postSensorDataAPI(
            {
              file_uid: fileUid,
              file_type: sensorType,
              project_id: params.project_id,
              latitude: params.latitude,
              longitude: params.longitude,
            },
            {
              plant: getFormDataItem(formData, 'plant'),
              use_plant_geometry: getFormDataItem(formData, 'use_plant_geometry'),
            },
          )
            .then((sensorData) => {
              // Update the pattern field with the fetched data
              updateFormValue(name, sensorData);
            })
            .catch((error) => {
              console.error('Error fetching sensor data:', error);

              // Clear the pattern data when there's an error to show empty graph instead of old data
              updateFormValue(name, null);

              handleError(error);
            });
        }
      }

      if (field.hasOwnProperty('onChange')) {
        field.onChange(data);
      }
      if (!modified) {
        setModified(true);
        setResults({});
      }

      if (data.warning) {
        toast(data.warning, {
          icon: '⚠️',
        });
      } else {
        toast.success('Soiling sensor data uploaded');
      }
    },
    onError: (error) => {
      handleError(error);
    },
  });

  const getFormDataItem = (data: any, name: string) => {
    return name.split('.').reduce((acc, key) => {
      return acc && acc.hasOwnProperty(key) ? acc[key] : '';
    }, data);
  };

  return (
    <FormContext.Provider
      value={{
        formData,
        setFormData,
        getFormDataItem,
        updateDataValue,
        updateFormValue,
        results,
        setResults,
        handleChange,
        handleSpecialArrayFieldChange,
        clearSoilingRatioSelections,
      }}
    >
      {children}
    </FormContext.Provider>
  );
};

export const useFormContext = (): FormContextType => {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error('useFormContext must be used within a FormContextProvider');
  }
  return context;
};

export default FormContextProvider;
